import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { supabase } from "@/integrations/supabase/client";
import { asVoucherResult } from "@/lib/rpcResults";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { Receipt, Mail, CheckCircle2 } from "lucide-react";
import { getCurrencyFromLocale, getCountryFromLocale } from "@/lib/locale-utils";
import { Footer } from "@/components/Footer";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import * as Sentry from "@sentry/react";

const createAuthSchema = (t: TFunction, isLogin: boolean) => z.object({
  email: z.string().email(t('auth.invalidEmail')),
  password: z.string().min(12, t('auth.passwordMinLength')),
  username: z.string().min(3, t('auth.usernameMinLength')).max(30, t('auth.usernameMaxLength')).optional(),
  termsAccepted: !isLogin ? z.boolean().refine(val => val === true, {
    message: "You must accept the terms and conditions to sign up",
  }) : z.boolean().optional(),
});

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [username, setUsername] = useState("");
  const [voucherCode, setVoucherCode] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    // Check if this is a password recovery
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const type = hashParams.get('type');

    if (type === 'recovery') {
      setIsResettingPassword(true);
      return;
    }

    // Check if user is already logged in
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session && !isResettingPassword && !signupSuccess) {
        navigate("/");
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsResettingPassword(true);
      } else if (session && !isResettingPassword && !signupSuccess) {
        navigate("/");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, isResettingPassword, signupSuccess]);

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate input
      const validationData = isLogin
        ? { email, password }
        : { email, password, username, termsAccepted };

      createAuthSchema(t, isLogin).parse(validationData);

      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;

        toast({
          title: t('auth.welcomeBack'),
          description: t('auth.loginSuccess'),
        });
      } else {
        // Determine the correct redirect URL based on environment
        const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const redirectUrl = isDevelopment ? `${window.location.origin}/` : 'https://app.cartxray.com/';

        // Get browser locale and derive currency/country
        const browserLocale = i18n.language;
        const defaultCurrency = getCurrencyFromLocale(browserLocale);
        const defaultCountry = getCountryFromLocale(browserLocale);

        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: redirectUrl,
            data: {
              username,
              preferred_language: browserLocale,
              default_currency: defaultCurrency,
              default_country: defaultCountry,
              terms_accepted: termsAccepted,
              terms_accepted_at: new Date().toISOString(),
              terms_version: '1.0',
            },
          },
        });

        if (error) throw error;

        // Apply voucher code if provided
        if (voucherCode.trim()) {
          try {
            // Get the newly created user's session to get their ID
            const { data: { session: newSession } } = await supabase.auth.getSession();
            if (newSession?.user) {
              const { data: rawVoucherResult, error: voucherError } = await supabase.rpc('apply_voucher', {
                p_user_id: newSession.user.id,
                p_code: voucherCode.trim().toUpperCase()
              });
              const voucherResult = asVoucherResult(rawVoucherResult);

              if (voucherError) {
                console.error("Failed to apply voucher:", voucherError);
                const sentryEventId = Sentry.captureException(voucherError, {
                  level: 'error',
                  tags: {
                    component: 'Auth',
                    action: 'apply_voucher',
                    error_code: '2002', // VOUCHER_APPLY_FAILED
                  },
                  extra: {
                    userId: newSession.user.id,
                    userEmail: email,
                    voucherCode: voucherCode.trim().toUpperCase(),
                    errorMessage: voucherError.message,
                  },
                });
                const supportId = sentryEventId
          ? sentryEventId.substring(0, 8).toUpperCase()
          : `DEV${Date.now().toString(36).toUpperCase().slice(-6)}`;
                toast({
                  title: t('errors.title'),
                  description: t('errors.voucherApplyFailedWithSupport', { supportId }),
                  variant: "destructive",
                  duration: Infinity,
                });
              } else if (voucherResult && !voucherResult.success) {
                const sentryEventId = Sentry.captureMessage('Invalid voucher code', {
                  level: 'warning',
                  tags: {
                    component: 'Auth',
                    action: 'apply_voucher',
                    error_code: '2003', // VOUCHER_INVALID
                  },
                  extra: {
                    userId: newSession.user.id,
                    userEmail: email,
                    voucherCode: voucherCode.trim().toUpperCase(),
                    errorMessage: voucherResult.error,
                  },
                });
                const supportId = sentryEventId
          ? sentryEventId.substring(0, 8).toUpperCase()
          : `DEV${Date.now().toString(36).toUpperCase().slice(-6)}`;
                toast({
                  title: t('errors.title'),
                  description: t('errors.voucherInvalidWithSupport', { supportId }),
                  variant: "destructive",
                  duration: Infinity,
                });
              } else if (voucherResult && voucherResult.success) {
                toast({
                  title: "Voucher Applied!",
                  description: `You received ${voucherResult.credits_added} free receipt credits!`,
                });
              }
            }
          } catch (voucherError) {
            console.error("Failed to apply voucher:", voucherError);
            // Don't block signup if voucher fails
          }
        }

        // Send welcome email and add to Brevo list (skip for test users)
        if (!email.endsWith('@test.com')) {
          try {
            await supabase.functions.invoke('welcome-user', {
              body: {
                email,
                username,
                language: browserLocale,
              },
            });
          } catch (emailError) {
            console.error("Failed to send welcome email:", emailError);
            // Don't block signup if email fails
          }
        }

        // Set signup success state to prevent auto-redirect and show success message
        setSignupSuccess(true);

        toast({
          title: t('auth.accountCreated'),
          description: t('auth.verifyEmailPrompt'),
        });
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: t('errors.title'),
          description: error.errors[0].message,
          variant: "destructive",
          duration: Infinity,
        });
      } else {
        const errorMessage = error instanceof Error ? error.message : t('errors.genericError');
        const sentryEventId = Sentry.captureException(error, {
          level: 'error',
          tags: {
            component: 'Auth',
            action: isLogin ? 'login' : 'signup',
            error_code: '3003', // AUTH_FAILED
          },
          extra: {
            email,
            isLogin,
            errorMessage,
          },
        });
        const supportId = sentryEventId
          ? sentryEventId.substring(0, 8).toUpperCase()
          : `DEV${Date.now().toString(36).toUpperCase().slice(-6)}`;
        toast({
          title: t('errors.title'),
          description: t('errors.authFailedWithSupport', { supportId }),
          variant: "destructive",
          duration: Infinity,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Determine the correct redirect URL based on environment
      const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const redirectUrl = isDevelopment ? `${window.location.origin}/auth` : 'https://app.cartxray.com/auth';

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (error) throw error;

      toast({
        title: t('auth.resetLinkSent'),
        description: t('auth.resetLinkDescription'),
      });
      setIsForgotPassword(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('errors.genericError');
      const sentryEventId = Sentry.captureException(error, {
        level: 'error',
        tags: {
          component: 'Auth',
          action: 'password_reset_request',
          error_code: '3004', // PASSWORD_RESET_REQUEST_FAILED
        },
        extra: {
          email,
          errorMessage,
        },
      });
      const supportId = sentryEventId
          ? sentryEventId.substring(0, 8).toUpperCase()
          : `DEV${Date.now().toString(36).toUpperCase().slice(-6)}`;
      toast({
        title: t('errors.title'),
        description: t('errors.passwordResetRequestFailedWithSupport', { supportId }),
        variant: "destructive",
        duration: Infinity,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast({
        title: t('errors.title'),
        description: t('errors.passwordMismatch'),
        variant: "destructive",
        duration: Infinity,
      });
      return;
    }

    if (newPassword.length < 12) {
      toast({
        title: t('errors.title'),
        description: t('auth.passwordMinLength'),
        variant: "destructive",
        duration: Infinity,
      });
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      toast({
        title: t('auth.passwordUpdated'),
        description: t('auth.passwordUpdatedDescription'),
      });

      // Sign out and redirect to login
      await supabase.auth.signOut();
      setIsResettingPassword(false);
      setIsLogin(true);
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('errors.genericError');
      const sentryEventId = Sentry.captureException(error, {
        level: 'error',
        tags: {
          component: 'Auth',
          action: 'password_update',
          error_code: '3005', // PASSWORD_UPDATE_FAILED
        },
        extra: {
          errorMessage,
        },
      });
      const supportId = sentryEventId
          ? sentryEventId.substring(0, 8).toUpperCase()
          : `DEV${Date.now().toString(36).toUpperCase().slice(-6)}`;
      toast({
        title: t('errors.title'),
        description: t('errors.passwordUpdateFailedWithSupport', { supportId }),
        variant: "destructive",
        duration: Infinity,
      });
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center p-4 gap-4">
        <div className="w-full max-w-md">
          <LanguageSwitcher />
        </div>
        <Card className="w-full max-w-md shadow-custom-lg border-0">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <div className={`p-3 ${signupSuccess ? 'bg-green-500' : ''} rounded-full`}>
              {signupSuccess ? (
                <CheckCircle2 className="h-8 w-8 text-white" />
              ) : (
                <Receipt className="h-8 w-8 text-white" />
              )}
            </div>
          </div>
          <CardTitle className="text-2xl font-bold">
            {signupSuccess
              ? t('auth.checkYourEmail')
              : (isResettingPassword
                ? t('buttons.updatePassword')
                : (isForgotPassword ? t('buttons.resetPassword') : (isLogin ? t('auth.welcomeBack') : t('buttons.signUp'))))}
          </CardTitle>
          <CardDescription>
            {signupSuccess
              ? t('auth.verificationEmailSent', { email })
              : (isResettingPassword
                ? t('auth.enterNewPassword')
                : (isForgotPassword
                  ? t('auth.enterEmail')
                  : (isLogin
                    ? t('common.appDescription')
                    : t('common.appDescription'))))}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {signupSuccess ? (
            <div className="space-y-6 py-4">
              <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="space-y-2">
                    <h3 className="font-semibold text-sm text-blue-900 dark:text-blue-100">
                      {t('auth.verifyToGetStarted')}
                    </h3>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      {t('auth.verificationInstructions')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3 text-sm text-muted-foreground">
                <p className="font-medium text-foreground">{t('auth.whatHappensNext')}</p>
                <ol className="space-y-2 list-decimal list-inside">
                  <li>{t('auth.checkInbox')}</li>
                  <li>{t('auth.clickVerificationLink')}</li>
                  <li>{t('auth.autoSignIn')}</li>
                  <li>{t('auth.startScanning')}</li>
                </ol>
              </div>

              <div className="pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground text-center">
                  {t('auth.didntReceiveEmail')}{" "}
                  <button
                    type="button"
                    onClick={() => {
                      setSignupSuccess(false);
                      setEmail("");
                      setPassword("");
                      setUsername("");
                      setVoucherCode("");
                    }}
                    className="text-primary hover:underline"
                  >
                    {t('buttons.trySignUpAgain')}
                  </button>
                </p>
              </div>
            </div>
          ) : isResettingPassword ? (
            <form onSubmit={handleUpdatePassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">{t('auth.newPassword')}</Label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder={t('auth.enterNewPassword')}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">{t('auth.confirmPassword')}</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder={t('auth.confirmNewPassword')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full transition-all duration-300"
                disabled={loading}
              >
                {loading ? t('buttons.updating') : t('buttons.updatePassword')}
              </Button>
            </form>
          ) : isForgotPassword ? (
            <form onSubmit={handlePasswordReset} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email">{t('auth.email')}</Label>
                <Input
                  id="reset-email"
                  type="email"
                  placeholder={t('auth.enterEmail')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full transition-all duration-300"
                disabled={loading}
              >
                {loading ? t('buttons.sending') : t('buttons.sendResetLink')}
              </Button>
              <div className="text-center text-sm">
                <button
                  type="button"
                  onClick={() => {
                    setIsForgotPassword(false);
                    setIsLogin(true);
                  }}
                  className="text-primary hover:underline"
                >
                  {t('buttons.signIn')}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleEmailAuth} className="space-y-4">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="username">{t('auth.username')}</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder={t('auth.enterUsername')}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required={!isLogin}
                />
              </div>
            )}
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="voucher-code">
                  Voucher Code <span className="text-xs text-muted-foreground">(Optional)</span>
                </Label>
                <Input
                  id="voucher-code"
                  type="text"
                  placeholder="Enter voucher code (e.g., FRIEND50)"
                  value={voucherCode}
                  onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                  className="uppercase"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t('auth.enterEmail')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Input
                id="password"
                type="password"
                placeholder={t('auth.enterPassword')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {!isLogin && (
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="terms"
                  checked={termsAccepted}
                  onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                  required
                />
                <Label
                  htmlFor="terms"
                  className="text-sm font-normal leading-tight cursor-pointer"
                >
                  {t('auth.iAcceptThe')}{" "}
                  <Link
                    to="/privacy-policy"
                    target="_blank"
                    className="text-primary hover:underline"
                  >
                    {t('auth.privacyPolicy')}
                  </Link>{" "}
                  {t('auth.andThe')}{" "}
                  <Link
                    to="/eula"
                    target="_blank"
                    className="text-primary hover:underline"
                  >
                    {t('auth.eula')}
                  </Link>
                </Label>
              </div>
            )}
            <Button
              type="submit"
              className="w-full transition-all duration-300"
              disabled={loading}
            >
              {loading ? t('common.loading') : (isLogin ? t('buttons.signIn') : t('buttons.signUp'))}
            </Button>
            {isLogin && (
              <div className="text-center text-sm">
                <button
                  type="button"
                  onClick={() => setIsForgotPassword(true)}
                  className="text-primary hover:underline"
                >
                  {t('auth.forgotPassword')}
                </button>
              </div>
            )}
          </form>
          )}

          {!isForgotPassword && !isResettingPassword && (
            <div className="text-center text-sm">
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-primary hover:underline"
              >
                {isLogin ? t('auth.noAccount') : t('auth.haveAccount')}
              </button>
            </div>
          )}
        </CardContent>
      </Card>
      </div>
      <Footer />
    </div>
  );
};

export default Auth;